import type { SearchClient } from 'algoliasearch';
import type { IntlShape } from 'react-intl/src/types';

import type {
  ProductTypeOptions,
  TranslatedProductType,
} from '@/types/product';
import { ProductTypeEnum } from '@/types/product';
import {
  replaceDotsWithUnderscores,
  replaceStringDotsWithUnderscores,
  replaceStringUnderscoresWithDots,
  replaceUnderscoresWithDots,
} from '@/utils/array';

import { initializeApollo } from '@/graphql/client';
import type {
  MyOrderLineFragment,
  ProductCardFragment,
  ProductMediaFragment,
  ProductType,
  ProductTypesSearchQuery,
  ProductTypesSearchQueryVariables,
  ProductUrlBaseFragment,
  ProductVariantEyewearBasicDetailsQuery,
  ProductVariantLineFragment,
  ProductVariantPriceQuery,
  ProductVariantWatchBasicDetailsQuery,
  SelectedAttribute,
  StrapAddToBagToolsQuery,
  StrapAddToBagToolsQueryVariables,
} from '@/graphql/saleor/api';
import {
  ProductTypesSearchDocument,
  StrapAddToBagToolsDocument,
} from '@/graphql/saleor/api';
import type { StrapAddToBagTool } from '@/hooks/useBucklesAndTools';
import {
  checkProductActivation,
  getProduct,
  getProducts,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';
import { NEXT_PUBLIC_IMAGES_FORMAT, THUMBNAIL_IMAGE_SIZE } from '@/lib/const';
import { isAbortLikeError } from '@/lib/errors';
import { mapEdgesToItems } from '@/lib/maps';
import { paths } from '@/lib/paths';
import type { CtaType, Maybe } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import type { RegionsProviderContext } from '@/providers/RegionProvider';
import type { CheckoutState } from '@/states/checkout';
import type { Watch } from '@/views/MyWatchesView';

import { getStrapMaterial } from './straps';

export const getVariantCTA = (
  formatMessage: IntlShape['formatMessage'],
  ctaType: CtaType | undefined,
) => {
  if (ctaType === 'ORDER') {
    return formatMessage(commonMessages.addToBag);
  } else if (ctaType === 'PRE_ORDER') {
    return formatMessage(commonMessages.preOrderNow);
  } else if (ctaType === 'NOTIFY') {
    return formatMessage(commonMessages.notifyMe);
  } else if (ctaType === 'DISCOVER') {
    return formatMessage(commonMessages.discover);
  } else if (ctaType === 'BOOK_APPOINTMENT') {
    return formatMessage(commonMessages.bookAppointment);
  } else if (ctaType === 'ENGRAVING') {
    return formatMessage(commonMessages.addEngraving);
  }

  return formatMessage(commonMessages.contactUs);
};

export const makeWatchProductUrl = (
  collection: string,
  collections: { name: string; slug: string }[],
  modelSlug: string,
  sku: string,
  asPath = false,
) => {
  const productCollections = collections;
  const productModelSlug = modelSlug;
  const productCollectionSlug = (
    productCollections.find(
      ({ slug }) => slug.toLowerCase() === collection.toLowerCase(),
    ) || productCollections[0]
  ).slug.toLowerCase();
  const urlOpts = {
    model: productModelSlug,
    collection: productCollectionSlug,
    sku,
  };

  if (asPath) {
    return paths.watches.collection.model.product.asPath(urlOpts);
  }
  return paths.watches.collection.model.product.url(urlOpts);
};

export const makeStrapProductUrl = (
  material: string,
  sku: string,
  asPath = false,
) => {
  if (!material) return '#';
  const dataMaterial = getStrapMaterial(material ?? 'unknown');
  const urlOpts = {
    material: dataMaterial,
    sku,
  };
  if (asPath) {
    return paths.straps.material.product.asPath(urlOpts);
  }
  return paths.straps.material.product.url(urlOpts);
};

export const makeEyewearProductUrl = (
  model: string,
  sku: string,
  asPath = false,
) => {
  if (!model) return '#';

  const eyewearModel = model ?? 'unknown';

  const urlOpts = {
    modelSku: `${eyewearModel}-${sku}`,
  };
  if (asPath) {
    return paths.sunglasses.product.asPath(urlOpts);
  }
  return paths.sunglasses.product.url(urlOpts);
};

export const generateProductWatchPdpPath = (
  data: Watch,
  sku: string,
  asPath = false,
) => {
  const urlOpts = {
    sn: data.sn,
    sku,
  };

  if (asPath) {
    return paths.myAccount.myWatches.detail.asPath(urlOpts);
  }
  return paths.myAccount.myWatches.detail.url(urlOpts);
};

export const getProductWatchPath = (
  product: ProductUrlBaseFragment,
  asPath = false,
  variantSku?: Maybe<string>,
) => {
  const collectionSlug = product.collections?.[0]?.slug;
  if (!collectionSlug) {
    return '#';
  }

  return makeWatchProductUrl(
    collectionSlug,
    product.collections ?? [],
    product.model?.values[0]?.slug ?? '',
    variantSku ?? product.defaultVariant!.sku!,
    asPath,
  );
};

export const getProductWatchPdpPath = (product: Watch, asPath = false) => {
  return generateProductWatchPdpPath(product, product.sku!, asPath);
};

export const getProductStrapPath = (
  product: ProductUrlBaseFragment,
  asPath = false,
  variantSku?: Maybe<string>,
) => {
  return makeStrapProductUrl(
    product.strapMaterial?.values[0]?.name ?? 'unknown',
    variantSku ?? product.defaultVariant!.sku!,
    asPath,
  );
};

export const getProductEyewearPath = (
  product: ProductUrlBaseFragment,
  asPath = false,
  variantSku?: Maybe<string>,
) => {
  if (!product.eyewearModel) return '#';

  const eyewearModel = product.eyewearModel?.values[0]?.slug ?? 'unknown';

  // Extract the base model name (e.g., "leman-0001" from "leman-0001-3")
  // Use a regular expression to remove numeric suffix after the last hyphen
  const baseModelName = eyewearModel.replace(/-\d+$/, '');

  const urlOpts = {
    modelSku: `${baseModelName}-${variantSku ?? product.defaultVariant!.sku!}`,
  };

  if (asPath) {
    return paths.sunglasses.product.asPath(urlOpts);
  }

  const url = paths.sunglasses.product.url(urlOpts);
  return url;
};

export const getProductPath = (
  product: ProductUrlBaseFragment,
  asPath = false,
  variantSku?: Maybe<string>,
) => {
  const productType = product.productType.slug;

  if (productType === 'strap') {
    return getProductStrapPath(product, asPath, variantSku);
  } else if (productType === 'watch') {
    return getProductWatchPath(product, asPath, variantSku);
  } else if (productType === 'eyewear') {
    return getProductEyewearPath(product, asPath, variantSku);
  }

  return '#';
};
export const getProductPdpPath = (product: Watch, asPath = false) => {
  return getProductWatchPdpPath(product, asPath);
};

interface TranslatedProductName {
  buckleName?: { values: { translatedName?: { plainText?: string } }[] };
  digitalItemName?: { values: { translatedName?: { plainText?: string } }[] };
  eyewearModel?: { values: { translatedName?: { plainText?: string } }[] };
  strapName?: { values: { translatedName?: { plainText?: string } }[] };
  toolName?: { values: { translatedName?: { plainText?: string } }[] };
  warrantyName?: { values: { translatedName?: { plainText?: string } }[] };
  watchName?: { values: { translatedName?: { plainText?: string } }[] };
}

export const isValidTranslatedProductType = (
  productType: string,
): productType is TranslatedProductType => {
  const validTypes: TranslatedProductType[] = [
    'watch',
    'strap',
    'buckle',
    'tool',
    'warranty',
  ];
  return validTypes?.includes(productType as TranslatedProductType);
};

export const getTranslatedProductName = (
  product:
    | ProductVariantLineFragment['product']
    | NonNullable<MyOrderLineFragment['variant']>['product'],
): string | undefined => {
  const productType = getProductType(product.productType);
  const validTypes: TranslatedProductType[] = [
    ProductTypeEnum.Watch,
    ProductTypeEnum.Strap,
    ProductTypeEnum.Buckle,
    ProductTypeEnum.Tool,
    ProductTypeEnum.Warranty,
    ProductTypeEnum.Eyewear,
    ProductTypeEnum.DigitalItem,
  ];
  if (!validTypes?.includes(productType as TranslatedProductType)) {
    if ('name' in product) {
      return product.name;
    }
    return undefined;
  }

  const type = productType as TranslatedProductType;

  const fieldMap: Record<TranslatedProductType, keyof TranslatedProductName> = {
    [ProductTypeEnum.Watch]: 'watchName',
    [ProductTypeEnum.Strap]: 'strapName',
    [ProductTypeEnum.Buckle]: 'buckleName',
    [ProductTypeEnum.Tool]: 'toolName',
    [ProductTypeEnum.Warranty]: 'warrantyName',
    [ProductTypeEnum.Eyewear]: 'eyewearModel',
    [ProductTypeEnum.DigitalItem]: 'digitalItemName',
  };

  const field = fieldMap[type];

  if (!product || !field) return undefined;

  return (
    product[field]?.values[0]?.translation?.plainText ??
    product[field]?.values[0]?.name ??
    undefined
  );
};

export const getProductType = (productType: Maybe<Pick<ProductType, 'slug'>>) =>
  productType?.slug?.toLowerCase() as ProductTypeOptions;

export const excludeProductType = (
  productType: Maybe<Pick<ProductType, 'slug'>>,
  excludedProductTypeSlug: ProductTypeOptions,
) => productType?.slug !== excludedProductTypeSlug;

export const excludeProductsType = (
  productType: Maybe<Pick<ProductType, 'slug'>>,
  excludedProductsTypeSlug: ProductTypeOptions[],
) =>
  !excludedProductsTypeSlug?.includes(productType?.slug as ProductTypeOptions);

export const getImageScaleRatio = (
  productType?: Maybe<Pick<ProductType, 'slug'>>,
) => {
  const productTypeSlug = productType?.slug?.toLowerCase();

  if (productTypeSlug === 'watch') {
    return 1.5;
  } else if (productTypeSlug === 'strap') {
    return 1.15;
  } else if (productTypeSlug === 'gift') {
    return 1;
  }

  return undefined;
};

/** for now WatchCard.tsx image scale is 1.2, StrapCard.tsx image scale is 1.0,
 ** not sure product Card still use getImageScaleFromDiameter or just hardcode 1.2/1
 **/
export const getImageScaleRatioByProduct = (product: ProductCardFragment) => {
  const productTypeSlug = product.productType?.slug?.toLowerCase();
  if (productTypeSlug === 'watch') {
    return 1.2;
  } else {
    return 1;
  }
};

export const toolSlug: ProductTypeOptions = 'tool';

export const fetchTools = async ({
  channel,
  languageCode,
}: RegionsProviderContext['query']): Promise<StrapAddToBagTool[]> => {
  const client = initializeApollo();
  let toolId;

  try {
    const { data } = await client.query<
      ProductTypesSearchQuery,
      ProductTypesSearchQueryVariables
    >({
      query: ProductTypesSearchDocument,
      variables: {
        first: 1,
        filter: { slugs: [toolSlug] },
      },
      fetchPolicy: 'cache-first',
    });
    toolId = data?.productTypes?.edges?.[0]?.node?.id;
  } catch (err) {
    if (isAbortLikeError(err)) {
      return [];
    }
    console.warn('[fetchTools] fetchTools failed:', err);
  }

  if (!toolId) {
    return [];
  }

  try {
    const { data } = await client.query<
      StrapAddToBagToolsQuery,
      StrapAddToBagToolsQueryVariables
    >({
      query: StrapAddToBagToolsDocument,
      variables: {
        limit: 1,
        filter: { productTypes: [toolId] },
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        channel,
        languageCode,
      },
    });

    const products = data?.products;
    if (!products) {
      return [];
    }
    return mapEdgesToItems(products).map(
      (item) => item.defaultVariant as StrapAddToBagTool,
    );
  } catch (err) {
    if (isAbortLikeError(err)) {
      return [];
    }
    console.warn('[fetchTools] fetchTools failed:', err);
    return [];
  }
};

export const isToolInShoppingBag = (checkout: CheckoutState['checkout']) =>
  !!checkout?.lines?.find(
    (line) => getProductType(line.variant.product.productType) === toolSlug,
  );

export type ImageShotType =
  | 'Soldier'
  | 'Three-quarter'
  | 'Rolled up'
  | 'Back'
  | 'On wrist';

export const findMediaByShotType = (
  medias: ProductMediaFragment[],
  shotType: ImageShotType,
) => {
  return medias.find((item) => item.shotType === shotType);
};

export const generateProductWatchCollectionPath = (
  collection: string,
  asPath = false,
) => {
  const urlOpts = {
    slug: collection,
  };
  if (asPath) {
    return paths.collections.asPath(urlOpts);
  }
  return paths.collections.url(urlOpts);
};

export const getWatchCollectionPath = (
  product: ProductUrlBaseFragment,
  asPath = false,
) => {
  const productType = product.productType.slug;

  if (productType === 'strap') {
    if (asPath) {
      return paths.straps.all.asPath();
    }
    return paths.straps.all.url();
  }

  const productCollectionSlug = product.collections?.[0]?.slug;

  return generateProductWatchCollectionPath(
    productCollectionSlug ?? '',
    asPath,
  );
};

export const parseDesignerChoice = (
  designerChoiceAttribute: SelectedAttribute | null | undefined,
) => {
  if (!designerChoiceAttribute) {
    return null;
  }
  const valueName = designerChoiceAttribute?.values[0]?.name || '';

  if (valueName?.includes('No')) {
    return false;
  } else if (valueName?.includes('Yes')) {
    return true;
  }

  return null;
};

export const extractDesignerChoiceSkuFromVariant = async (
  data: ProductVariantWatchBasicDetailsQuery,
  locale: string | undefined,
  client: SearchClient,
): Promise<string | null> => {
  const designerChoiceAttribute = data?.productVariant?.product
    ?.designerChoice as SelectedAttribute;
  const isDesignerChoice = parseDesignerChoice(designerChoiceAttribute);

  if (isDesignerChoice) {
    return data.productVariant?.sku ?? null;
  }
  const bundleVariations = data?.productVariant?.product.bundleVariations;

  if (!bundleVariations?.values || bundleVariations.values.length === 0) {
    return data.productVariant?.sku ?? null;
  }
  const candidateSkus = bundleVariations.values
    .map((v) => v?.name)
    .filter((sku): sku is string => !!sku);

  const candidateProducts = await getProducts<WatchHit>(
    locale,
    candidateSkus,
    client,
  );

  for (const product of candidateProducts) {
    if (!product || !product.designer_choice) continue;
    const isActiveProduct = checkProductActivation(
      product,
      false,
    ).isPdpActivated;
    if (isActiveProduct) {
      return product.objectID;
    }
  }
  return data.productVariant?.sku ?? null;
};

export async function makeWatchCanonicalFromVariant(
  data: ProductVariantWatchBasicDetailsQuery,
  model: string,
  collection: string,
  locale: string | undefined,
  client: SearchClient,
) {
  const designerChoiceSku = await extractDesignerChoiceSkuFromVariant(
    data,
    locale,
    client,
  );

  if (
    designerChoiceSku !== null &&
    designerChoiceSku !== data?.productVariant?.sku
  ) {
    return paths.watches.collection.model.product.asPath({
      model: model,
      collection: collection,
      sku: designerChoiceSku as string,
    });
  }
  return 'watch';
}

export const updateProductVariantPricing = <
  T extends
    | ProductVariantWatchBasicDetailsQuery
    | ProductVariantEyewearBasicDetailsQuery,
>(
  data: T,
  variantProductPrice?: ProductVariantPriceQuery,
): T => {
  if (
    variantProductPrice &&
    data.productVariant &&
    data.productVariant?.pricing === null
  ) {
    return {
      ...data,
      productVariant: {
        ...data.productVariant,
        pricing: variantProductPrice.productVariant?.pricing ?? null,
      },
    };
  } else {
    return data;
  }
};

export const skuToSlug = (slug: string) =>
  replaceStringDotsWithUnderscores(slug);

export const slugToSku = (sku: string) => replaceStringUnderscoresWithDots(sku);

export const slugsToSkus = (slugs: string[]) =>
  replaceUnderscoresWithDots(slugs);

export const skusToSlugs = (skus: string[]) => replaceDotsWithUnderscores(skus);

export const isProductPersonalizedEngraving = (
  product: ProductVariantLineFragment['product'],
) => {
  const hasEngravingMedia = product.media?.some(
    (media) =>
      typeof media.backEngravingCoordinates === 'string' &&
      media.backEngravingCoordinates.length > 0,
  );

  const isProductEngravingEnabled =
    product.personalizedEngraving?.values[0]?.boolean === true;

  return hasEngravingMedia && isProductEngravingEnabled;
};

export async function resolveStrapCanonical(
  params: {
    dataMaterial: string;
    draftMode?: boolean;
    locale: string;
    otherSizesSkus: string[];
    sku: string;
  },
  options: { currentStrapHit?: StrapHit | null } = {},
): Promise<string> {
  const { dataMaterial, draftMode, locale, otherSizesSkus, sku } = params;
  const current = options.currentStrapHit;

  // Self by default
  const canonicalUrl = 'strap';

  // If current is Standard, keep self
  const currentIsStandard =
    (current?.strap_length_en || '').toLowerCase() === 'standard';
  if (currentIsStandard) return canonicalUrl;

  const algoliaSsrSearchClient = createSearchClient({
    isPreview: draftMode,
    isSsr: true,
  });

  // Iterate over known SKUs (current + other sizes), find an active Standard
  const candidateSkus = [sku, ...otherSizesSkus];
  for (const candidate of candidateSkus) {
    const product = await getProduct<StrapHit>(
      locale,
      candidate,
      algoliaSsrSearchClient,
    );
    const isStandard =
      (product?.strap_length_en || '').toLowerCase() === 'standard';
    if (!isStandard) continue;

    const isActive = checkProductActivation(
      product,
      !!draftMode,
    ).isPdpActivated;
    if (!isActive) continue;

    return makeStrapProductUrl(dataMaterial, candidate, true) as string;
  }

  return canonicalUrl;
}
